/**
 * Role-Based Route Guard
 * Protects routes that require specific roles
 */

import { Navigate } from "react-router-dom";
import { type ReactNode } from "react";
import { useAuthStore } from "../stores/auth.store";
import { usePermissions } from "../hooks/usePermissions";

type RoleBasedRouteProps = {
  children: ReactNode;
  requiredPermission: (permissions: ReturnType<typeof usePermissions>) => boolean;
  redirectTo?: string;
};

/**
 * RoleBasedRoute Component
 * Wraps routes that require specific role-based permissions
 */
export const RoleBasedRoute = ({ 
  children, 
  requiredPermission,
  redirectTo = "/"
}: RoleBasedRouteProps) => {
  const { isAuthenticated, isInitialized } = useAuthStore();
  const permissions = usePermissions();

  // Wait for auth initialization before checking authentication
  if (!isInitialized) {
    return null; // or a loading spinner
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has required permission
  if (!requiredPermission(permissions)) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};
